/**
 * Inbox list filters: status → Marketplace API lastTransitions,
 * plus helpers for client-side text search.
 */
import {
  getProcess,
  getSupportedProcessesInfo,
  PURCHASE_PROCESS_NAME,
  BOOKING_PROCESS_NAME,
} from '../../transactions/transaction';

/**
 * Stable URL values for ?status=
 * Mapped to process states on purchase and/or booking graphs.
 */
export const INBOX_STATUS_FILTERS = [
  {
    value: 'inquiry',
    labelId: 'InboxPage.filters.status.inquiry',
    statesByProcess: {
      [PURCHASE_PROCESS_NAME]: ['inquiry'],
      [BOOKING_PROCESS_NAME]: ['inquiry'],
    },
  },
  {
    value: 'pending-payment',
    labelId: 'InboxPage.filters.status.pendingPayment',
    statesByProcess: {
      [PURCHASE_PROCESS_NAME]: ['pending-payment'],
      [BOOKING_PROCESS_NAME]: ['pending-payment'],
    },
  },
  {
    value: 'payment-expired',
    labelId: 'InboxPage.filters.status.paymentExpired',
    statesByProcess: {
      [PURCHASE_PROCESS_NAME]: ['payment-expired'],
      [BOOKING_PROCESS_NAME]: ['payment-expired'],
    },
  },
  {
    // Purchase "Waiting for delivery" + booking "Accepted" / requested fulfillment
    value: 'purchased',
    labelId: 'InboxPage.filters.status.waitingForDelivery',
    statesByProcess: {
      [PURCHASE_PROCESS_NAME]: ['purchased', 'preauthorized'],
      [BOOKING_PROCESS_NAME]: ['accepted', 'preauthorized'],
    },
  },
  {
    value: 'delivered',
    labelId: 'InboxPage.filters.status.delivered',
    statesByProcess: {
      [PURCHASE_PROCESS_NAME]: ['delivered'],
    },
  },
  {
    value: 'waiting-for-review',
    labelId: 'InboxPage.filters.status.waitingForReview',
    statesByProcess: {
      [PURCHASE_PROCESS_NAME]: ['received', 'completed'],
      [BOOKING_PROCESS_NAME]: ['delivered'],
    },
  },
  {
    value: 'reviewed-by-customer',
    labelId: 'InboxPage.filters.status.reviewedByCustomer',
    statesByProcess: {
      [PURCHASE_PROCESS_NAME]: ['reviewed-by-customer'],
      [BOOKING_PROCESS_NAME]: ['reviewed-by-customer'],
    },
  },
  {
    value: 'reviewed-by-provider',
    labelId: 'InboxPage.filters.status.reviewedByProvider',
    statesByProcess: {
      [PURCHASE_PROCESS_NAME]: ['reviewed-by-provider'],
      [BOOKING_PROCESS_NAME]: ['reviewed-by-provider'],
    },
  },
  {
    value: 'reviewed',
    labelId: 'InboxPage.filters.status.completed',
    statesByProcess: {
      [PURCHASE_PROCESS_NAME]: ['reviewed'],
      [BOOKING_PROCESS_NAME]: ['reviewed'],
    },
  },
  {
    value: 'canceled',
    labelId: 'InboxPage.filters.status.canceled',
    statesByProcess: {
      [PURCHASE_PROCESS_NAME]: ['canceled'],
      [BOOKING_PROCESS_NAME]: ['canceled'],
    },
  },
  {
    value: 'declined',
    labelId: 'InboxPage.filters.status.declined',
    statesByProcess: {
      [PURCHASE_PROCESS_NAME]: ['declined'],
      [BOOKING_PROCESS_NAME]: ['declined'],
    },
  },
  {
    value: 'disputed',
    labelId: 'InboxPage.filters.status.disputed',
    statesByProcess: {
      [PURCHASE_PROCESS_NAME]: ['disputed'],
    },
  },
  {
    value: 'expired',
    labelId: 'InboxPage.filters.status.expired',
    statesByProcess: {
      [PURCHASE_PROCESS_NAME]: ['expired'],
      [BOOKING_PROCESS_NAME]: ['expired'],
    },
  },
];

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export const isTransactionUuid = value => UUID_RE.test(String(value || '').trim());

/**
 * Resolve lastTransitions for a status filter value across supported processes.
 * Returns null when status is empty/unknown (caller should use all transitions).
 */
export const getLastTransitionsForInboxStatus = status => {
  if (!status) {
    return null;
  }
  const filter = INBOX_STATUS_FILTERS.find(f => f.value === status);
  if (!filter) {
    return null;
  }

  const processNames = getSupportedProcessesInfo().map(p => p.name);
  const transitions = new Set();

  processNames.forEach(processName => {
    const states = filter.statesByProcess[processName];
    if (!states?.length) {
      return;
    }
    try {
      const process = getProcess(processName);
      process.getTransitionsToStates(states).forEach(t => transitions.add(t));
    } catch (e) {
      // Unknown / unavailable process — skip
    }
  });

  return transitions.size ? Array.from(transitions) : null;
};

const displayNameOf = user =>
  user?.attributes?.profile?.displayName || user?.attributes?.profile?.abbreviatedName || '';

/**
 * Client-side match for search box: counterparty name, listing/event title,
 * listing id, or transaction UUID.
 */
export const transactionMatchesInboxSearch = (tx, query, { isOrders } = {}) => {
  const q = String(query || '')
    .trim()
    .toLowerCase();
  if (!q) {
    return true;
  }

  const id = tx?.id?.uuid || '';
  if (id.toLowerCase().includes(q)) {
    return true;
  }

  const listingId = tx?.listing?.id?.uuid || '';
  if (listingId.toLowerCase().includes(q)) {
    return true;
  }

  const otherUser = isOrders ? tx?.provider : tx?.customer;
  const name = displayNameOf(otherUser).toLowerCase();
  if (name.includes(q)) {
    return true;
  }

  const title = String(tx?.listing?.attributes?.title || '').toLowerCase();
  if (title.includes(q)) {
    return true;
  }

  const publicData = tx?.listing?.attributes?.publicData || {};
  const eventName = String(publicData.eventName || publicData.event_name || '').toLowerCase();
  if (eventName.includes(q)) {
    return true;
  }

  return false;
};
